import React, { useContext, useEffect, useMemo } from "react";
import { Map, Buildings, Check } from "@styled-icons/boxicons-regular";
import { MdInfoOutline } from "react-icons/md";
import { observer } from "mobx-react";
import { toJS } from "mobx";
import { ArrowBack } from "@styled-icons/evaicons-solid";
import { useHistory } from "react-router-dom";

import BaronaLogo from "b2c/components/BaronaLogo";
import BlackLogo from "images/b2b_invite_logo_fuzu.svg";
import {
  Content,
  Container,
  Header,
  JobTitle,
  JobInfo,
  Applying,
  BenefitTitle,
  JoinNow,
  Submit,
  Divider,
  InfoMessage,
  CompanyLogo,
  CompanyInfo,
  GuestButton,
  ContentSection,
  SectionTitle,
  BenefitsList,
  BenefitItem,
  ApplicationCard,
  Flex,
  Label,
  LoginButton,
  LoginLink,
  BackTo
} from "./styled";
import ThemeContext from "../../contexts/theme";
import useStore from "../../contexts/store";
import { useTranslation } from "../../hooks/useTranslation";
import { useIsBarona, useLanguageAndCountryUrl } from "../../hooks";
import { Spinner } from "../../components/Core";

const ExternalApplication = ({
  match: {
    params: { id }
  }
}) => {
  const { t } = useTranslation();
  const theme = useContext(ThemeContext);
  const { getPosition, job_details, submitATSJob, state } = useStore("JobSearch");
  const jobDetails = useMemo(() => toJS(job_details), [job_details.title]);
  const languageAndLanguage = useLanguageAndCountryUrl();
  const { user } = useStore("User");
  const { global_brand: globalBrand } = useStore("initialState");
  const isBarona = useIsBarona();
  const history = useHistory();

  const externalUrl = useMemo(
    () => jobDetails.external_fields?.external_url,
    [jobDetails.external_fields?.external_url]
  );

  const onboardingPath = useMemo(() => {
    return isBarona
      ? `${languageAndLanguage}/onboarding?return_to=/external-application/${id}&application_id=${id}&isExternalTypeJob=true`
      : `/onboarding/your-email?shorterFlow=true&return_to=/external-application/${id}&application_id=${id}&isExternalTypeJob=true`;
  }, [externalUrl]);

  const loginPath = useMemo(() => {
    return `/login?return_to=/external-application/${id}&isExternalTypeJob=true`;
  }, [externalUrl]);

  const handleSignIn = () => {
    window.location.href = globalBrand.custom_authentication_b2c_url;
  };

  const handleSubmit = () => {
    submitATSJob(jobDetails.id).then(res => {
      if (res) {
        history.push("/job");
        window.open(jobDetails.external_fields?.external_url, "_blank");
      }
    });
  };

  useEffect(() => {
    getPosition(id);
  }, []);

  return (
    <Container $isSetHeight={user}>
      <Header>
        <BaronaLogo>
          <BlackLogo />
        </BaronaLogo>
      </Header>

      <Content>
        <ApplicationCard>
          <Applying>{t("jobs.external.applying_for")}</Applying>
          <JobInfo>
            <CompanyLogo>
              <img src={jobDetails?.company_logo || ""} alt="" />
            </CompanyLogo>
            <div>
              <JobTitle>{jobDetails.title}</JobTitle>
              <CompanyInfo>
                <Buildings />
                {jobDetails.company_name}
              </CompanyInfo>

              {jobDetails.location ? (
                <CompanyInfo>
                  <Map />
                  {jobDetails.location}
                </CompanyInfo>
              ) : null}
            </div>
          </JobInfo>

          <InfoMessage>
            <MdInfoOutline />
            {t("jobs.external.info")}
          </InfoMessage>
        </ApplicationCard>

        {!user ? (
          <>
            <ContentSection>
              <SectionTitle>{t("jobs.external.sign_up.title")}</SectionTitle>

              <BenefitTitle>{t("jobs.external.sign_up.sub_title")}</BenefitTitle>

              <BenefitsList>
                <BenefitItem>
                  <Check />
                  <span>{t("jobs.external.sign_up.label_1")}</span>
                </BenefitItem>
                <BenefitItem>
                  <Check />
                  <span>{t("jobs.external.sign_up.label_2")}</span>
                </BenefitItem>
                <BenefitItem>
                  <Check />
                  <span>{t("jobs.external.sign_up.label_3")}</span>
                </BenefitItem>
              </BenefitsList>
            </ContentSection>

            <JoinNow
              to={onboardingPath}
              $background={theme.primaryButtonBackground}
              $color={theme.primaryButtonLabelColor}
            >
              {t("global.joinNow")}
            </JoinNow>

            <Flex>
              <Label>{t("global.already_have_an_account")}</Label>
              {isBarona ? (
                <LoginButton onClick={handleSignIn}>{t("global.sign_in")}</LoginButton>
              ) : (
                <LoginLink to={loginPath}>{t("global.sign_in")}</LoginLink>
              )}
            </Flex>

            <Divider>{t("jobs.external.sign_up.or")}</Divider>

            <GuestButton href={externalUrl} rel="noopener noreferrer">
              {t("jobs.external.sign_up.continue_as_guest")}
            </GuestButton>
          </>
        ) : (
          <>
            <Submit
              onClick={handleSubmit}
              $background={theme.primaryButtonBackground}
              $color={theme.primaryButtonLabelColor}
              disabled={state.loading}
            >
              {t("jobs.external.apply_on_company")}

              {state.loading ? <Spinner size="24px" /> : null}
            </Submit>
            <BackTo to="/job">
              <ArrowBack />
              {t("jobs.external.back_to")}
            </BackTo>
          </>
        )}
      </Content>
    </Container>
  );
};

export default observer(ExternalApplication);
